import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Organization, OrgPart, OrgHat, OrgUser, OrgSecurityGroup, OrgPolicy } from '@newgenus/common';
import { Component, Inject, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../material.module';
import { OrgEntityColorsService } from '../../../org-dashboard/services/org-entity-colors.service';
import { ProfileColorService } from '../../services/profile-color.service';
import { OrgDialogService } from '../../services/org-dialog.service';
import { OrgBoardService } from '../../services/org-board-service';
import { Subject, takeUntil } from 'rxjs';
import { LinkOrigin, linkOrigin } from '../../linked-posts';

// Organization features that trigger system route access (any of these grants access to default org pages)
const ORG_SYSTEM_FEATURES = [
    'organization',
    'organization-dashboard',
    'organization-settings',
    'organization-integrations',
    'organization-targets',
    'organization-dates',
];

// Management tools features that trigger system route access (any of these grants access to dashboard)
const MGMT_SYSTEM_FEATURES = [
    'management-tools',
    'management-tools-dashboard',
    'statistics',
    'checklist',
    'client-abc',
];

// Menu configuration for preview (mirrors the app's sidebar menu structure)
const MENU_ITEMS: { feature: string; name: string; icon: string; section: string }[] = [
    // Home section (top-level navigation)
    { feature: 'workspace', name: 'WorkSpace', icon: 'account_balance_wallet', section: 'Home' },
    { feature: 'management-tools-dashboard', name: 'Management Tools', icon: 'engineering', section: 'Home' },
    { feature: 'organization', name: 'Organization', icon: 'corporate_fare', section: 'Home' },
    // Workspace section
    { feature: 'workspace', name: 'Inbasket', icon: 'inbox', section: 'Workspace' },
    { feature: 'workspace', name: 'Pending', icon: 'pending', section: 'Workspace' },
    { feature: 'workspace', name: 'Outbasket', icon: 'outbox', section: 'Workspace' },
    { feature: 'workspace', name: 'Filed', icon: 'task_alt', section: 'Workspace' },
    { feature: 'workspace', name: 'Search', icon: 'search', section: 'Workspace' },
    // Management Tools
    { feature: 'management-tools-dashboard', name: 'Dashboard', icon: 'dashboard', section: 'Management Tools' },
    { feature: 'checklist', name: 'Checklist', icon: 'checklist', section: 'Management Tools' },
    { feature: 'statistics', name: 'Statistics', icon: 'analytics', section: 'Management Tools' },
    { feature: 'client-abc', name: 'Client ABC', icon: 'hotel_class', section: 'Management Tools' },
    // Organization (system routes - accessible with any org feature via system route inheritance)
    { feature: 'organization', name: 'Dashboard', icon: 'dashboard', section: 'Organization' },
    { feature: 'organization', name: 'My Hats', icon: 'school', section: 'Organization' },
    { feature: 'organization', name: 'My Policies', icon: 'fact_check', section: 'Organization' },
    { feature: 'organization', name: 'Org Board', icon: 'account_tree', section: 'Organization' },
    { feature: 'organization-targets', name: 'Targets', icon: 'multiline_chart', section: 'Organization' },
    { feature: 'organization-dates', name: 'Date Cycles', icon: 'event', section: 'Organization' },
    {
        feature: 'organization-integrations',
        name: 'Integrations',
        icon: 'integration_instructions',
        section: 'Organization',
    },
    { feature: 'organization', name: 'Manage', icon: 'settings_applications', section: 'Organization' },
    { feature: 'organization-settings', name: 'Settings', icon: 'settings', section: 'Organization' },
];

export interface PositionDetailsDialogData {
    organization: Organization;
    position: OrgPart;
}

interface RelatedHat {
    key: string;
    name: string;
    userCount: number;
}

interface RelatedUser {
    key: string;
    name: string;
    email: string;
    viaHat: string;
}

interface RelatedSecurityGroup {
    key: string;
    name: string;
    viaHat: string;
}

interface RelatedPolicy {
    key: string;
    name: string;
    viaHat: string;
}

interface ChildPosition {
    key: string;
    name: string;
    partType: string;
    colour: { background: string; color: string };
}

@Component({
    selector: 'shared-org-position-details-dialog',
    imports: [MaterialModule, CommonModule],
    templateUrl: './org-position-details-dialog.html',
    styleUrls: ['../org-dialog.scss', '../../organization-board.component.scss', './org-position-details-dialog.scss'],
})
export class OrgPositionDetailsDialogComponent implements OnInit, OnDestroy {
    public entityColors = inject(OrgEntityColorsService);
    private profileColorService = inject(ProfileColorService);
    public orgDialogService = inject(OrgDialogService);
    private orgBoardService = inject(OrgBoardService);

    position!: OrgPart;
    parentPosition: OrgPart | null = null;
    childPositions: ChildPosition[] = [];
    relatedHats: RelatedHat[] = [];
    relatedUsers: RelatedUser[] = [];
    pendingUsers: RelatedUser[] = [];
    relatedSecurityGroups: RelatedSecurityGroup[] = [];
    relatedPolicies: RelatedPolicy[] = [];

    // Menu preview
    public menuPreview: { section: string; items: { name: string; icon: string }[] }[] = [];

    // Section collapse states
    public sectionStates = {
        children: true,
        hats: true,
        users: true,
        securityGroups: true,
        policies: true,
        menuPreview: true,
    };

    // VFP editing state
    public isEditingVfp = false;

    private destroy$ = new Subject<void>();

    constructor(
        public dialogRef: MatDialogRef<OrgPositionDetailsDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PositionDetailsDialogData,
        private dialog: MatDialog
    ) {
        // Full-bleed on phones (DESIGN.md §Sheets, OE-153 item 4) — the class
        // is phone-scoped globally, desktop unaffected.
        this.dialogRef.addPanelClass('ng-fullbleed-dialog');
    }

    ngOnInit(): void {
        console.log('PositionDetailsDialog > data:', this.data);
        console.log('PositionDetailsDialog > position:', this.data.position);
        console.log('PositionDetailsDialog > org orgParts:', this.data.organization?.orgParts?.length);
        this.position = this.data.position;
        this.loadRelationships();
        console.log('PositionDetailsDialog > after loadRelationships:', {
            childPositions: this.childPositions.length,
            relatedHats: this.relatedHats.length,
            relatedUsers: this.relatedUsers.length,
            relatedSGs: this.relatedSecurityGroups.length,
            relatedPolicies: this.relatedPolicies.length,
        });
        this.subscribeToOrgUpdates();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private subscribeToOrgUpdates(): void {
        this.orgBoardService.organization$.pipe(takeUntil(this.destroy$)).subscribe((org) => {
            if (!org) return;
            this.data.organization = org;
            // Update position reference from latest orgParts
            const updated = org.orgParts?.find(
                (p) => (p.partKey || p.key) === (this.position.partKey || this.position.key)
            );
            if (updated) this.position = updated;
            this.loadRelationships();
        });
    }

    /**
     * Get initials from a name for avatar display
     */
    public getInitials(name: string): string {
        if (!name) return '?';
        const parts = name.trim().split(/\s+/);
        if (parts.length === 1) {
            return parts[0].substring(0, 2).toUpperCase();
        }
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }

    /**
     * Gets the avatar URL for a user.
     * Returns photoThumbnail if available, otherwise falls back to generated avatar.
     */
    public getUserAvatarUrl(user: RelatedUser): string {
        const org = this.data.organization;

        // Check organization's orgUsers for photoThumbnail
        if (org?.orgUsers?.[user.key]?.photoThumbnail) {
            return org.orgUsers[user.key].photoThumbnail!;
        }

        // Fall back to generated avatar
        const name = user.name || 'User';
        return this.profileColorService.getAvatarUrl(user.key, name, 40);
    }

    /**
     * Get sidebar gradient based on position color or default blue
     */
    public getSidebarGradient(): string {
        const bg = this.position?.colour?.background || this.entityColors.getColor('positions');
        // Create a darker shade for gradient
        return `linear-gradient(135deg, ${bg} 0%, ${this.darkenColor(bg, 15)} 100%)`;
    }

    private darkenColor(hex: string, percent: number): string {
        // Remove # if present
        hex = hex.replace('#', '');

        // Parse RGB
        let r = parseInt(hex.substring(0, 2), 16);
        let g = parseInt(hex.substring(2, 4), 16);
        let b = parseInt(hex.substring(4, 6), 16);

        // Darken
        r = Math.max(0, Math.floor((r * (100 - percent)) / 100));
        g = Math.max(0, Math.floor((g * (100 - percent)) / 100));
        b = Math.max(0, Math.floor((b * (100 - percent)) / 100));

        // Convert back to hex
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    }

    private loadRelationships(): void {
        const org = this.data.organization;
        const orgParts = org?.orgParts || [];
        const positionKey = this.position.partKey || this.position.key || '';

        // Build part map
        const partMap = new Map<string, OrgPart>();
        for (const part of orgParts) {
            const key = part.partKey || part.key || '';
            if (key) partMap.set(key, part);
        }

        // Get parent position
        if (this.position.parentPartKey) {
            this.parentPosition = partMap.get(this.position.parentPartKey) || null;
        }

        // Get child positions
        this.childPositions = orgParts
            .filter((part) => part.parentPartKey === positionKey)
            .map((part) => ({
                key: part.partKey || part.key || '',
                name: part.name || part.mainTitle || '',
                partType: part.partType || '',
                colour: part.colour || { background: '#4fc3f7', color: '#fff' },
            }));

        // Get hats at this position and related entities
        const orgHats = org?.orgHats;
        const orgUsers = org?.orgUsers;
        const orgPolicies = org?.orgPolicies;

        const userSet = new Map<string, RelatedUser>();
        const sgSet = new Map<string, RelatedSecurityGroup>();
        const policySet = new Map<string, RelatedPolicy>();

        if (orgHats?.hatsKeys) {
            for (const hatKey of orgHats.hatsKeys) {
                const hat = orgHats[hatKey] as OrgHat;
                if (!hat) continue;

                const positionArray = hat.positionArray || [];
                if (!positionArray.includes(positionKey)) continue;

                const hatName = hat.name || hatKey;

                // Add hat
                const users = hat.assignedToArray || hat.userKeys || [];
                this.relatedHats.push({
                    key: hatKey,
                    name: hatName,
                    userCount: users.length,
                });

                // Add users via this hat
                for (const userKey of users) {
                    if (userSet.has(userKey)) continue;
                    const user = orgUsers?.[userKey] as OrgUser;
                    userSet.set(userKey, {
                        key: userKey,
                        name: user?.userName || user?.name || userKey,
                        email: user?.email || '',
                        viaHat: hatName,
                    });
                }

                // Add security groups via this hat
                const sgArray = hat.securityGroupsArray || hat.securityGroups || [];
                const orgSGs = org?.orgSecurityGroups;
                for (const sg of sgArray) {
                    // sg can be a string key OR an object with .key
                    const sgKey = typeof sg === 'string' ? sg : sg?.key || '';
                    if (!sgKey || sgSet.has(sgKey)) continue;
                    const sgData = orgSGs?.[sgKey] as OrgSecurityGroup | undefined;
                    sgSet.set(sgKey, {
                        key: sgKey,
                        name: sgData?.name || sgKey,
                        viaHat: hatName,
                    });
                }

                // Add policies via this hat
                const policyKeysObj = hat.policyKeys || {};
                for (const policyKey of Object.keys(policyKeysObj)) {
                    if (policySet.has(policyKey)) continue;
                    const policy = orgPolicies?.[policyKey] as OrgPolicy;
                    policySet.set(policyKey, {
                        key: policyKey,
                        name: policy?.name || policyKey,
                        viaHat: hatName,
                    });
                }
            }
        }

        this.relatedUsers = Array.from(userSet.values());
        this.relatedSecurityGroups = Array.from(sgSet.values());
        this.relatedPolicies = Array.from(policySet.values());

        // Collect pending users from hats at this position
        const pendingUserSet = new Map<string, RelatedUser>();
        if (orgHats?.hatsKeys) {
            for (const hatKey of orgHats.hatsKeys) {
                const hat = orgHats[hatKey] as OrgHat;
                if (!hat) continue;
                const positionArray = hat.positionArray || [];
                if (!positionArray.includes(positionKey)) continue;
                const pendingArr = hat.pendingAssignedToArray || [];
                for (const userKey of pendingArr) {
                    if (userSet.has(userKey) || pendingUserSet.has(userKey)) continue;
                    const user = orgUsers?.[userKey] as OrgUser;
                    pendingUserSet.set(userKey, {
                        key: userKey,
                        name: user?.userName || user?.name || userKey,
                        email: user?.email || '',
                        viaHat: hat.name || hatKey,
                    });
                }
            }
        }
        this.pendingUsers = Array.from(pendingUserSet.values());

        // Ensure profile colors are loaded
        this.profileColorService.ensureLoadedMany([
            ...this.relatedUsers.map((u) => u.key),
            ...this.pendingUsers.map((u) => u.key),
        ]);

        // Compute menu preview from all features across security groups at this position
        this.computeMenuPreview(sgSet, org);
    }

    /**
     * Compute which menu items users at this position would see,
     * based on the features from all security groups attached via hats.
     */
    private computeMenuPreview(sgSet: Map<string, RelatedSecurityGroup>, org: Organization): void {
        const allFeatures = new Set<string>();
        const orgSGs = org?.orgSecurityGroups;

        // Collect features from each security group at this position
        for (const [sgKey] of sgSet) {
            const sg = orgSGs?.[sgKey] as OrgSecurityGroup | undefined;
            if (sg?.features) {
                for (const f of sg.features) {
                    allFeatures.add(f);
                }
            }
            // Also check featureDetails keys as fallback
            if (sg?.featureDetails) {
                for (const fKey of Object.keys(sg.featureDetails)) {
                    allFeatures.add(fKey);
                }
            }
        }

        // Handle system route inheritance: if ANY org feature is present, add 'organization' parent
        // This mirrors the system route behavior where My Hats, My Policies, Org Board, Dashboard, and Manage
        // are accessible with any organization-* feature
        if (ORG_SYSTEM_FEATURES.some((f) => allFeatures.has(f))) {
            allFeatures.add('organization');
        }

        // Handle management tools system route: if ANY mgmt feature is present, add 'management-tools-dashboard'
        // This mirrors the system route behavior where Dashboard is accessible with any mgmt feature
        if (MGMT_SYSTEM_FEATURES.some((f) => allFeatures.has(f))) {
            allFeatures.add('management-tools-dashboard');
        }

        // Filter menu items by available features
        const accessibleItems = MENU_ITEMS.filter((item) => allFeatures.has(item.feature));

        // Group by section (deduplicate by name)
        const grouped = new Map<string, { name: string; icon: string }[]>();
        for (const item of accessibleItems) {
            if (!grouped.has(item.section)) {
                grouped.set(item.section, []);
            }
            const existing = grouped.get(item.section) || [];
            if (!existing.some((e) => e.name === item.name)) {
                existing.push({ name: item.name, icon: item.icon });
            }
        }

        // Convert to array with proper section ordering
        const sectionOrder = ['Home', 'Workspace', 'Management Tools', 'Organization'];
        this.menuPreview = sectionOrder
            .filter((section) => grouped.has(section))
            .map((section) => ({
                section,
                items: grouped.get(section) || [],
            }));
    }

    viewParent(): void {
        if (this.parentPosition) {
            this.dialog.open(OrgPositionDetailsDialogComponent, {
                data: {
                    organization: this.data.organization,
                    position: this.parentPosition,
                },
                height: '85%',
                width: '85%',
                maxHeight: 'unset',
                maxWidth: 'unset',
            });
        }
    }

    viewChildPosition(child: ChildPosition): void {
        const orgParts = this.data.organization?.orgParts || [];
        const part = orgParts.find((p) => (p.partKey || p.key) === child.key);
        if (part) {
            this.dialog.open(OrgPositionDetailsDialogComponent, {
                data: {
                    organization: this.data.organization,
                    position: part,
                },
                height: '85%',
                width: '85%',
                maxHeight: 'unset',
                maxWidth: 'unset',
            });
        }
    }

    viewHat(hat: RelatedHat): void {
        import('../org-hat-details-dialog').then((m) => {
            const orgHats = this.data.organization?.orgHats;
            const hatData = orgHats?.[hat.key] as OrgHat;
            if (hatData) {
                this.dialog.open(m.OrgHatDetailsDialogComponent, {
                    data: {
                        organization: this.data.organization,
                        hat: hatData,
                        hatKey: hat.key,
                    },
                    height: '85%',
                    width: '85%',
                    maxHeight: 'unset',
                    maxWidth: 'unset',
                });
            }
        });
    }

    viewUser(user: RelatedUser): void {
        import('../org-user-details-dialog').then((m) => {
            const userData = this.data.organization?.orgUsers?.[user.key] as OrgUser;
            if (userData) {
                this.dialog.open(m.OrgUserDetailsDialogComponent, {
                    data: {
                        organization: this.data.organization,
                        user: userData,
                        userKey: user.key,
                    },
                    height: '85%',
                    width: '85%',
                    maxHeight: 'unset',
                    maxWidth: 'unset',
                });
            }
        });
    }

    viewSecurityGroup(sg: RelatedSecurityGroup): void {
        import('../org-security-group-details-dialog').then((m) => {
            const sgData = this.data.organization?.orgSecurityGroups?.[sg.key] as OrgSecurityGroup;
            if (sgData) {
                this.dialog.open(m.OrgSecurityGroupDetailsDialogComponent, {
                    data: {
                        organization: this.data.organization,
                        securityGroup: sgData,
                        securityGroupKey: sg.key,
                    },
                    height: '85%',
                    width: '85%',
                    maxHeight: 'unset',
                    maxWidth: 'unset',
                });
            }
        });
    }

    viewPolicy(policy: RelatedPolicy): void {
        import('../org-policy-details-dialog').then((m) => {
            const policyData = this.data.organization?.orgPolicies?.[policy.key] as OrgPolicy;
            if (policyData) {
                this.dialog.open(m.OrgPolicyDetailsDialogComponent, {
                    data: {
                        organization: this.data.organization,
                        policy: policyData,
                        policyKey: policy.key,
                    },
                    height: '85%',
                    width: '85%',
                    maxHeight: 'unset',
                    maxWidth: 'unset',
                });
            }
        });
    }

    openExplorer(): void {
        import('../org-position-explorer-dialog').then((m) => {
            this.dialog.open(m.OrgPositionExplorerDialogComponent, {
                data: {
                    organization: this.data.organization,
                    selectedPositionKey: this.position.partKey || this.position.key,
                },
                height: '85%',
                width: '85%',
                maxHeight: 'unset',
                maxWidth: 'unset',
            });
        });
    }

    /**
     * Open the edit position dialog
     */
    public openEditDialog(): void {
        this.dialogRef.close();
        import('../org-position-dialog/org-edit-position-dialog/org-edit-position-dialog.component').then((m) => {
            this.dialog.open(m.OrgEditPositionDialogComponent, {
                data: {
                    part: this.position,
                    partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                    organization: this.data.organization,
                },
                width: '80%',
                height: '85%',
                minHeight: '300px',
                maxWidth: '98vw',
                panelClass: 'ng-resizable-dialog',
            });
        });
    }

    // VFP editing methods
    public startEditingVfp(): void {
        this.isEditingVfp = true;
    }

    public cancelEditingVfp(): void {
        this.isEditingVfp = false;
    }

    public async saveVfp(value: string): Promise<void> {
        this.isEditingVfp = false;
        const partKey = this.position?.partKey || this.position?.key;
        if (!partKey) return;

        // Update local state
        this.position.vfp = value;

        // Save to Firestore
        try {
            await this.orgBoardService.updatePart({ partKey, vfp: value } as any, this.data.organization, false);
            console.log('[position-details] VFP saved successfully for part:', partKey);
        } catch (error) {
            console.error('[position-details] Failed to save VFP:', error);
        }
    }

    // ═══ HFA participation toggle ═══

    /** Only Posts are HFA sources / can hold from above. */
    public get isPost(): boolean {
        return (this.position as any)?.partType === 'post';
    }

    /** true = participates in running the org (an HFA source). */
    public get holdsFromAbove(): boolean {
        return !(this.position as any)?.excludeFromHfa;
    }

    /**
     * Toggles whether this post is held from above onto positions below. OFF =
     * `excludeFromHfa:true` (board/governance seat). Rebuilds the board so HFA
     * recomputes immediately and persists via updatePart.
     */
    public async onHoldsFromAboveChange(checked: boolean): Promise<void> {
        const partKey = this.position?.partKey || this.position?.key;
        if (!partKey) return;
        const excludeFromHfa = !checked;

        (this.position as any).excludeFromHfa = excludeFromHfa;
        const orgPart = this.data.organization?.orgParts?.find((p: any) => p.partKey === partKey);
        if (orgPart) (orgPart as any).excludeFromHfa = excludeFromHfa;

        this.orgBoardService.partSettingChanged.next({ partKey, changes: { excludeFromHfa } });

        try {
            await this.orgBoardService.updatePart({ partKey, excludeFromHfa } as any, this.data.organization, false);
        } catch (error) {
            console.error('[position-details] Failed to save excludeFromHfa:', error);
        }
    }

    // ═══ Linked post ═══

    // A linked post REPRESENTS another post and comes from there: shown read-only (name + path),
    // never set or changed here — links come only from the migration (Paul, 2026-10-21).
    private _originCache: { parts: unknown; self: unknown; origin: LinkOrigin | null } | null = null;
    public get origin(): LinkOrigin | null {
        const parts = this.data.organization?.orgParts;
        const self = this.position?.partKey || this.position?.key;
        if (!this._originCache || this._originCache.parts !== parts || this._originCache.self !== self) {
            this._originCache = { parts, self, origin: linkOrigin((parts as any[]) || [], self) };
        }
        return this._originCache.origin;
    }

    /** "Go to origin": close the dialog and let the board scroll to + open the represented post. */
    public goToOrigin(): void {
        const target = this.origin?.partKey;
        if (!target) return;
        this.dialogRef.close();
        this.orgBoardService.navigateToPart.next(target);
    }
}
